/**
 * The two papers a school photographs back into a production record
 * (PROD-1511): the kitchen's Orders sheet, which travels with the food and is
 * signed at drop-off, and the production record's own PREP COPY, filled in
 * during service. One upload per paper on the phone and on the desktop; each
 * is read by its own task and the two answers fold into one summary here.
 *
 * Pure — no React, no fetch.
 */

import type { ScanSheetResponse } from '@/api/sheet-scan'
import { formatServeHeaderDateLabel, serviceDateToDate } from '@/lib/cafeteria/format'
import type { ScanPartialSummary } from '@/lib/sheet-scan/apply-result'

export type SheetPaper = 'delivery' | 'record'

/** The photos queued under each paper's upload. */
export interface SheetPhotos {
  delivery: File[]
  record: File[]
}

export const EMPTY_SHEET_PHOTOS: SheetPhotos = { delivery: [], record: [] }

/** The papers in the order the uploads show them: the delivery comes first in the day. */
export const SHEET_PAPERS: readonly SheetPaper[] = ['delivery', 'record']

/** Each upload's title, and the name a warning about its photos leads with. */
export const SHEET_PAPER_NAME: Record<SheetPaper, string> = {
  delivery: 'Delivery sheet',
  record: 'Production record',
}

/** Every photo queued across both uploads. */
export function sheetPhotoCount(photos: SheetPhotos): number {
  return photos.delivery.length + photos.record.length
}

/** "Read 3 photos": the primary under the uploads. */
export function readPhotosLabel(count: number): string {
  if (count === 0) {
    return 'Read photos'
  }
  return count === 1 ? 'Read 1 photo' : `Read ${count} photos`
}

/** One paper's applied answer, as the flow hands it to `combineScanParts`. */
export interface ScanSheetPart {
  /** `SHEET_PAPER_NAME[paper]`. */
  paper: string
  summary: ScanPartialSummary
  /** How many of `summary.warnings` lead with a rejected-photo line (they come first). */
  photoWarningCount: number
  /** Photos the device could not send (too large, unsupported), already phrased. */
  deviceErrors: string[]
  /** Cells this paper could fill on the targets. */
  total: number
  timeReadingApplied: boolean
}

/** Photos the reader judged to be a different sheet: the head of `summary.warnings`. */
export function countPhotoWarnings(result: ScanSheetResponse): number {
  return result.images.filter((image) => !image.verification.matchesExpected).length
}

export interface CombinedScanSummary extends ScanPartialSummary {
  totalCount: number
  timeReadingApplied: boolean
}

/**
 * Fold the papers' answers into the one summary the uploads report. A photo
 * line ("Photo 2 looks like a different sheet") names its paper once two
 * papers were sent, since "Photo 2" alone no longer says which upload; a
 * paper that could not be read at all becomes one warning, never a failure of
 * the other.
 */
export function combineScanParts(
  parts: readonly ScanSheetPart[],
  failures: ReadonlyArray<{ paper: string; reason: unknown }>,
): CombinedScanSummary {
  const named = parts.length + failures.length > 1
  const lead = (paper: string, line: string) => (named ? `${paper}: ${line}` : line)
  const warnings: string[] = []
  const unmatched: string[] = []
  const seen = new Set<string>()
  let filledCount = 0
  let totalCount = 0
  let lowConfidenceCount = 0
  let timeReadingApplied = false
  for (const part of parts) {
    warnings.push(...part.deviceErrors.map((line) => lead(part.paper, line)))
    warnings.push(...part.summary.warnings.slice(0, part.photoWarningCount).map((line) => lead(part.paper, line)))
    warnings.push(...part.summary.warnings.slice(part.photoWarningCount))
    filledCount += Math.min(part.summary.filledCount, part.total)
    totalCount += part.total
    lowConfidenceCount += part.summary.lowConfidenceCount
    timeReadingApplied ||= part.timeReadingApplied
    for (const name of part.summary.unmatchedRowsSeen) {
      const key = name.toLowerCase()
      if (!seen.has(key)) {
        seen.add(key)
        unmatched.push(name)
      }
    }
  }
  for (const failure of failures) {
    const message = failure.reason instanceof Error && failure.reason.message ? ` ${failure.reason.message}` : ''
    warnings.push(`Could not read the ${failure.paper.toLowerCase()} photos.${message}`)
  }
  return { filledCount, totalCount, lowConfidenceCount, warnings, unmatchedRowsSeen: unmatched, timeReadingApplied }
}

// ---------------------------------------------------------------------------
// The words each upload says about its paper
// ---------------------------------------------------------------------------

/** "A", "A and B", "A, B and C". */
export function joinWithAnd(parts: readonly string[]): string {
  if (parts.length <= 1) {
    return parts[0] ?? ''
  }
  return `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)!}`
}

/** The delivery dates behind these steps' trucks, `YYYY-MM-DD`, oldest first, each once. */
export function stepDeliveryDates(steps: ReadonlyArray<{ trucks: ReadonlyArray<{ deliveryDate: string }> }>): string[] {
  return [...new Set(steps.flatMap((step) => step.trucks.map((truck) => truck.deliveryDate.slice(0, 10))))]
    .filter((date) => serviceDateToDate(date) !== null)
    .sort()
}

/** "Oct 8". */
function monthDayLabel(iso: string): string {
  const date = serviceDateToDate(iso)
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : iso
}

/**
 * The phone's note under the Delivery sheet upload: which deliveries the
 * record covers, so a school with two drops photographs both sheets. Null when
 * no truck is known.
 */
export function deliverySheetNote(dates: readonly string[]): string | null {
  if (dates.length === 0) {
    return null
  }
  const labels = dates.map((date) => formatServeHeaderDateLabel(date))
  if (labels.length === 1) {
    return `From the delivery on ${labels[0]!}.`
  }
  return `This record covers ${labels.length} deliveries, ${joinWithAnd(labels)}. Add the sheet from each.`
}

/** The desktop strip's one line under "Delivery sheet". */
export function deliveryStripLine(dates: readonly string[]): string {
  const labels = dates.map(monthDayLabel)
  if (labels.length === 0) {
    return 'Orders sheet from the kitchen.'
  }
  if (labels.length === 1) {
    return `Orders sheet from the kitchen, delivered ${labels[0]!}.`
  }
  return `Orders sheet from the kitchen. One per delivery: ${joinWithAnd(labels)}.`
}

/** The phone's line after the production record's description: which pages to photograph. */
export function recordSheetDetail(titles: readonly string[]): string | null {
  if (titles.length === 0) {
    return null
  }
  return titles.length === 1 ? `Photograph the ${titles[0]!} page.` : `One page per mealtime: ${joinWithAnd(titles)}.`
}

/** The desktop strip's one line under "Production record", for the open mealtime. */
export function recordStripLine(title: string): string {
  return `The PREP COPY printed from Ordo. ${title}'s page.`
}
